<template>
  <div class="p-3 bg-white rounded shadow-sm" data-test="result-orchestrator" :data-orchestrator="`result_${reference}`">
    <ResultCeremonyActions
      :title="titleLabel"
      resource-type="result_orchestrators"
      :resource-id="resultOrchestrator.id"
      :can-retry="!isFinished && resultOrchestrator.status !== 'mapping'"
      :can-reset="(!isFinished && resultOrchestrator.status !== 'mapping') || resultOrchestrator.error"
    />
    <div class="small text-muted">{{ referenceLabel }}</div>

    <div class="accent-line mt-2 mb-4"></div>

    <div v-if="resultOrchestrator.error">
      <details class="alert alert-warning mb-2">
        <summary class="fw-bold">{{ t("js.election_client.error.headers.an_error_occurred") }}</summary>
        <div>
          <p class="mb-0">{{ t("backend.e2e.voting_round_reports.show.error_body") }}</p>
        </div>
      </details>
    </div>

    <div v-if="isFinished">
      <ResultOrchestratorCeremonyDetails
        :trustees="trustees"
        :result-orchestrator="resultOrchestrator"
      />
    </div>
    <div v-else>
      <ResultOrchestratorCalculatingResults
        :result-orchestrator="resultOrchestrator"
        :trustees="trustees"
        :threshold="threshold"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ResultCeremonyActions from "@/components/backend/result_orchestrator/ResultCeremonyActions.vue";
import ResultOrchestratorCeremonyDetails from "@/components/backend/result_orchestrator/ResultOrchestratorCeremonyDetails.vue";
import ResultOrchestratorCalculatingResults from "@/components/backend/result_orchestrator/ResultOrchestratorCalculatingResults.vue";
import { useI18n } from "vue-i18n";
import type {
  ConferenceContest,
  ElectaTrustee,
  PropType,
  E2E,
  E2EVoterGroup,
  SupportedLocale,
} from "@/types";

const { locale, t } = useI18n();

const props = defineProps({
  resultOrchestrator: {
    type: Object as PropType<E2E.ResultOrchestrator>,
    required: true,
  },
  votingRoundReport: {
    type: Object as PropType<E2E.VotingRoundReport>,
    required: true,
  },
  reference: {
    type: String,
    required: true,
  },
  trustees: {
    type: Array as PropType<Array<ElectaTrustee>>,
    required: true,
  },
  threshold: {
    type: Number,
    required: true,
  },
  extractionGrouping: {
    type: String as PropType<"contest" | "ballot">,
    required: true,
  },
  contest: {
    type: Object as PropType<ConferenceContest>,
    default: undefined,
  },
  voterGroup: {
    type: Object as PropType<E2EVoterGroup>,
    default: undefined,
  },
});

const isFinished = computed(() => props.resultOrchestrator.status === "finished");

const titleLabel = computed(() => props.extractionGrouping === "ballot"
  ? `${t("backend.e2e.voting_round_reports.show.voter_group_name")}: ${props.voterGroup?.name ?? ""}`
  : `${t("backend.e2e.voting_round_reports.show.contest_title")}: ${props.contest?.title?.[locale.value as SupportedLocale] ?? ""}`);

const referenceLabel = computed(() => props.extractionGrouping === "ballot"
  ? `${t("backend.e2e.voting_round_reports.show.voter_group_reference")}: ${props.reference}`
  : `${t("backend.e2e.voting_round_reports.show.contest_reference")}: ${props.reference}`);
</script>
